import React from 'react';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '@/components/ui/sheet';
import {
    DS_COLORS,
    DS_TYPOGRAPHY,
    DS_SPACING,
} from '@/config/ui-invariants';

/**
 * AccrezeoSheet — brand-aligned wrapper over shadcn Sheet.
 *
 * Replaces the hand-rolled `position: fixed` + `translateX` panels scattered
 * across the app (InboxDetailRenderer, FounderDetailPanel, custom agent-page
 * detail views) with one shared slide-in built on Radix Dialog.
 *
 * Animation, focus trap, Escape handling, click-outside, and body-scroll lock
 * come free from the Radix primitive. Visual tokens (palette, typography,
 * spacing, border, shadow) come from DS_* — zero inline hex.
 *
 * Props:
 *   open          — boolean, controls the sheet
 *   onClose       — close handler, fires on Escape, overlay click, X button
 *   title         — string. When no `header` is passed, rendered visibly in
 *                   the default header block. Always wired to Radix SheetTitle
 *                   for screen readers — never omit this for a11y, even if
 *                   visually invisible via `header`.
 *   subtitle      — optional string below title (DS_TYPOGRAPHY.bodySm)
 *   side          — 'right' | 'left' | 'top' | 'bottom' (default 'right')
 *   width         — CSS width for right/left sides (default '580px')
 *   footer        — optional React node rendered in a sticky footer
 *   header        — optional React node. When provided, replaces the default
 *                   header block entirely — AccrezeoSheet applies NO padding,
 *                   bg, or border around it (caller owns layout). A visually
 *                   hidden SheetTitle is still rendered for Radix a11y.
 *   topSlot       — optional React node rendered between the header and the
 *                   scrollable body. Non-scrolling, zero padding.
 *   ariaLabel     — string; falls back to title
 *   children      — body content (wrapped in a scrolling container with card padding)
 *   bodyPadding   — boolean (default true). Set false when the child needs
 *                   edge-to-edge layout.
 *   contentClassName — optional extra classes on the SheetContent primitive
 */
export function AccrezeoSheet({
    open,
    onClose,
    title,
    subtitle,
    side = 'right',
    width = '580px',
    footer,
    header,
    topSlot,
    ariaLabel,
    bodyPadding = true,
    contentClassName,
    children,
}) {
    const handleOpenChange = (nextOpen) => {
        if (!nextOpen) onClose?.();
    };

    const useVerticalSide = side === 'top' || side === 'bottom';
    const sideStyle = useVerticalSide ? { width: '100%' } : { width, maxWidth: '100vw' };
    const usingCustomHeader = header !== undefined && header !== null;

    // Visually-hidden style preserves Radix a11y (DialogTitle requirement)
    // when a custom header renders the visible title itself.
    const visuallyHidden = {
        position: 'absolute',
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
    };

    return (
        <Sheet open={open} onOpenChange={handleOpenChange}>
            <SheetContent
                side={side}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel || title || 'Detail panel'}
                className={contentClassName}
                style={{
                    ...sideStyle,
                    padding: 0,
                    background: DS_COLORS.surface.white,
                    borderColor: DS_COLORS.border.default,
                    boxShadow: DS_COLORS.shadow.card,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                }}
            >
                {usingCustomHeader ? (
                    <>
                        <SheetTitle style={visuallyHidden}>
                            {ariaLabel || title || 'Detail panel'}
                        </SheetTitle>
                        <div style={{ flexShrink: 0 }}>{header}</div>
                    </>
                ) : (
                    <SheetHeader
                        style={{
                            padding: `${DS_SPACING.cardPadding}px`,
                            borderBottom: `1px solid ${DS_COLORS.border.default}`,
                            flexShrink: 0,
                            textAlign: 'left',
                            display: 'block',
                            margin: 0,
                        }}
                    >
                        <SheetTitle
                            style={title ? {
                                ...DS_TYPOGRAPHY.listItemTitle,
                                color: DS_COLORS.text.primary,
                                margin: 0,
                                paddingRight: `${DS_SPACING.minTouchTarget}px`,
                            } : visuallyHidden}
                        >
                            {title || ariaLabel || 'Detail panel'}
                        </SheetTitle>
                        {subtitle && (
                            <SheetDescription
                                style={{
                                    ...DS_TYPOGRAPHY.bodySm,
                                    color: DS_COLORS.text.secondary,
                                    marginTop: '4px',
                                }}
                            >
                                {subtitle}
                            </SheetDescription>
                        )}
                    </SheetHeader>
                )}

                {topSlot && (
                    <div style={{ flexShrink: 0 }}>
                        {topSlot}
                    </div>
                )}

                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: bodyPadding ? `${DS_SPACING.cardPadding}px` : 0,
                    }}
                >
                    {children}
                </div>

                {footer && (
                    <div
                        style={{
                            padding: `${DS_SPACING.cardPadding / 1.5}px ${DS_SPACING.cardPadding}px`,
                            borderTop: `1px solid ${DS_COLORS.border.default}`,
                            background: DS_COLORS.surface.white,
                            flexShrink: 0,
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                        }}
                    >
                        {footer}
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

export default AccrezeoSheet;
